import {faTable} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Calendar, CalendarProps} from 'antd';
import {CalendarMode} from 'antd/lib/calendar/generateCalendar';
import dayjs, {Dayjs} from 'dayjs';
import {ReactNode, useCallback, useMemo, useState} from 'react';

import {appRoute} from '../../../../app-route';
import {AlertFallback} from '../../../../component/alert-fallback/alert-fallback';
import {PageHeaderWithLicenseTag} from '../../../../component/header/header-with-license-tag/page-header-with-license-tag';
import {Meta} from '../../../../component/meta/meta';
import {BreadCrumbs} from '../../../../layout/bread-crumbs/bread-crumbs';
import {NavigationLink} from '../../../../layout/navigation-link/navigation-link';
import {Page} from '../../../../layout/page/page';
import {MainPageContainer} from '../../../../layout/page-card/main-page-card';
import {PageCard} from '../../../../layout/page-card/page-card';
import {Spinner} from '../../../../layout/spinner/spinner';
import {useLicenses} from '../../../../provider/license/license-hook';
import {Locale} from '../../../../provider/locale/locale';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {usePostsCalendar} from '../../../../service/posts/posts-hooks';
import {usePublicationsCalendar} from '../../../../service/publications/publications-calendar-hook';
import {FeaturesEnum} from '../../../../service/user/user-type';
import {classNames} from '../../../../util/css';
import * as postsStyles from '../posts.scss';
import {PostsCreatePublicationButton} from '../posts-create-publication-button/posts-create-publication-button';

import {STATUS_MODIFIER_BY_CALENDAR_POST_STATUS, TEXT_IN_YEAR_CARD_BY_POST_STATUS} from './posts-calendar-const';
import {PostsCalendarDayCard} from './posts-calendar-day-card';
import {PostsCalendarDayDrawer} from './posts-calendar-day-drawer';
import {
    extractPostsByIndexForMonthView,
    extractPostsByIndexForYearView,
    mergeCalendarSources,
} from './posts-calendar-helper';
import {PostsCalendarLegend} from './posts-calendar-legend';
import {CalendarPanelType} from './posts-calendar-type';
import * as styles from './posts-calendar.scss';

export function PostsCalendar(): JSX.Element {
    const {getLocalizedString} = useLocale();

    const [panel, setPanel] = useState<CalendarPanelType>({date: dayjs(), mode: 'month'});

    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);

    const {
        result: postsResult,
        isInProgress: isPostsInProgress,
        processError,
        reload: reloadPosts,
    } = usePostsCalendar(panel);

    // A failing publications request must not take the calendar down: posts still render, the
    // Promotions/Events entries are simply absent for that period.
    const {
        result: publicationsResult,
        isInProgress: isPublicationsInProgress,
        reload: reloadPublications,
    } = usePublicationsCalendar(panel);

    const {licenses} = useLicenses();
    const license = licenses[FeaturesEnum.posts];
    const isLicenseExpired = !license?.isActive;

    const calendarPosts = useMemo(
        () => mergeCalendarSources(postsResult, publicationsResult),
        [postsResult, publicationsResult]
    );

    const isInProgress = isPostsInProgress || isPublicationsInProgress;

    const selectedDateEntries = useMemo(
        () => (selectedDate ? extractPostsByIndexForMonthView(calendarPosts, selectedDate.date()) ?? [] : []),
        [calendarPosts, selectedDate]
    );

    function handlePanelChange(date: Dayjs, mode: CalendarMode) {
        setPanel({date, mode});
        // The drawer resolves its entries by day number against the loaded period, so a selected date
        // left over from another month would list the wrong day's publications under its own title.
        setSelectedDate(null);
    }

    // A new publication lands in the period currently on screen, so both sources are refetched rather
    // than waiting for the panel to move.
    function handleCreateSuccess() {
        reloadPosts?.();
        reloadPublications?.();
    }

    function handleDateSelect(date: Dayjs, info: Parameters<NonNullable<CalendarProps<Dayjs>['onSelect']>>[1]) {
        // if we are on year mode and try to change the year then we must stay on year mode
        if (panel.mode === 'year' && panel.date.year() !== date.year()) {
            return;
        }

        if (panel.mode === 'year') {
            setPanel({date, mode: 'month'});

            return;
        }

        // `onSelect` also fires for the header's month/year pickers; only a click on a day cell should
        // open the drawer.
        if (info.source !== 'date') {
            return;
        }

        // Days spilling in from a neighbouring month render empty (see `renderDateCell`) and their
        // publications are not in the loaded period at all — the drawer would look them up by day
        // number and show this month's entries. Such a click moves to that month instead.
        if (!date.isSame(panel.date, 'month')) {
            setPanel({date, mode: 'month'});

            return;
        }

        setSelectedDate(date);
    }

    const renderDateCell = useCallback(
        (date: Dayjs): ReactNode => {
            const isSameMonth = date.month() === panel.date.month();

            if (!isSameMonth) {
                return null;
            }

            const posts = extractPostsByIndexForMonthView(calendarPosts, date.date());

            if (!posts) {
                return null;
            }

            return <PostsCalendarDayCard entries={posts} />;
        },
        [calendarPosts, panel]
    );

    const renderMonthCell = useCallback(
        (date: Dayjs): ReactNode => {
            const posts = extractPostsByIndexForYearView(calendarPosts, date.month() + 1);

            if (!posts) {
                return null;
            }

            return (
                <ul className={styles.PostCalendar_list}>
                    {posts.map((post) => {
                        const text = getLocalizedString(TEXT_IN_YEAR_CARD_BY_POST_STATUS[post.status], {
                            count: String(post.count),
                        });

                        return (
                            <li className={styles.PostCalendar_monthCardItem} key={post.status} title={String(text)}>
                                <span
                                    className={classNames(
                                        styles.PostCalendar_dot,
                                        STATUS_MODIFIER_BY_CALENDAR_POST_STATUS[post.status]
                                    )}
                                />

                                <span className={styles.PostCalendar_entryName}>{text}</span>
                            </li>
                        );
                    })}
                </ul>
            );
        },
        [calendarPosts, getLocalizedString]
    );

    const renderCell = useCallback(
        (date: Dayjs, info: Parameters<NonNullable<CalendarProps<Dayjs>['cellRender']>>[1]): ReactNode => {
            if (info.type === 'month') {
                return renderMonthCell(date);
            }

            if (info.type === 'date') {
                return renderDateCell(date);
            }

            return null;
        },
        [renderDateCell, renderMonthCell]
    );

    return (
        <Page isTopFilterHidden renderContainer={(pageContent) => <MainPageContainer>{pageContent}</MainPageContainer>}>
            <Meta title={getLocalizedString('CATEGORY_NAME__POSTS_CALENDAR')} />

            {processError ? (
                <AlertFallback />
            ) : (
                <>
                    <PageCard>
                        <BreadCrumbs
                            list={[
                                {path: appRoute.postsManagement.path, titleLangKey: 'CATEGORY_NAME__POSTS'},
                                {path: appRoute.postsManagementCalendar.path, titleLangKey: 'POSTS__CALENDAR_HEADER'},
                            ]}
                        />

                        <PageHeaderWithLicenseTag
                            headerLangKey="POSTS__CALENDAR_HEADER"
                            licenseActive={license?.isActive}
                            trialLicense={license?.isTrial}
                        >
                            <div className={postsStyles.posts__header__button_wrapper}>
                                <NavigationLink className={styles.PostCalendar_link} to={appRoute.postsManagement.path}>
                                    <Button
                                        className={styles.PostCalendar_button}
                                        disabled={isLicenseExpired}
                                        icon={<FontAwesomeIcon className={styles.PostCalendar_icon} icon={faTable} />}
                                        type="default"
                                    >
                                        <Locale stringKey="POSTS__TABLE_PAGE" />
                                    </Button>
                                </NavigationLink>

                                <PostsCreatePublicationButton
                                    isLicenseExpired={isLicenseExpired}
                                    onCreateSuccess={handleCreateSuccess}
                                />
                            </div>
                        </PageHeaderWithLicenseTag>

                        <PostsCalendarLegend />
                    </PageCard>

                    <PageCard>
                        <div className={styles.PostCalendar_content}>
                            <Calendar
                                cellRender={renderCell}
                                className={classNames(styles.PostCalendar_calendar, {
                                    [styles.PostCalendar_calendar__yearMode]: panel.mode === 'year',
                                })}
                                mode={panel.mode}
                                onPanelChange={handlePanelChange}
                                onSelect={handleDateSelect}
                                value={panel.date}
                            />

                            {isInProgress && <Spinner className={styles.PostCalendar_spinner} position="absolute" />}
                        </div>
                    </PageCard>

                    <PostsCalendarDayDrawer
                        date={selectedDate}
                        entries={selectedDateEntries}
                        onClose={() => setSelectedDate(null)}
                    />
                </>
            )}
        </Page>
    );
}
